import type Phaser from "phaser";
import { COMPILED_COLLISION_CELLS_PER_TILE, frameId, type ManifestObjectAppearance, type OrderedProp, type WorldManifest } from "daedalus-core";
import { PropView, type TileAnimator } from "daedalus-renderer-phaser";
import { EntityKind, SIXTEENTHS, SOLID, ThingPlayback, isCreatureKind, isNpcKind, observeSnapshot, restampThings, thingGroundY, thingLook, thingPlace, type EntityState, type SnapshotRecord, type ThingStamp } from "odyssey-shared";
import type { LoadedWorld } from "../editor/world-loader.ts";
import { invalidateTarget, watchMoves, type TargetLookup } from "./interact-target.ts";
import { gameReport } from "./report.ts";
import { ShrineLooks } from "./shrine-looks.ts";
import { ThingViews } from "./thing-views.ts";
import type { MutablePoint } from "./world-ui/projection.ts";
import type { ThingSource } from "./world-ui/things.ts";

/**
 * Told of a thing's change of state the page saw: its handle, its appearance, undefined when the table lacks it, the
 * state index it left and the one it took.
 */
export type ThingTransitionListener = (handle: number, look: ManifestObjectAppearance | undefined, from: number, to: number) => void;

/**
 * The things the page draws (ADR-053): every entity in the client's view that is no player, creature or ground item
 * (players and creatures are drawn as characters, ground items by `ground/view.ts`) drawn as a prop at the frame
 * its appearance and state name, its footprint stamped over the prediction grid, and its transitions played over
 * the resting frame (ADR-058). The views it draws in join the prop views the occluder fade and the cover report
 * see. The cone of sight never dims a thing, in any place (ADR-061's amendment for P1.9-T82): a player walks up to a
 * sign, a well or a gate to use it, so the things' views are made as views the cone passes over, and they do not
 * count toward the cover that gates it; a thing that hides the local character still fades as a tree does. It is
 * also the scene's `ThingSource`: where the top of each thing's art is drawn, which a number dealt to a
 * training dummy rises from. A shrine is the one thing whose look the wire's state does not decide (ADR-096 decision
 * 16): it is placed and drawn on the frame this viewer's own set names (`ShrineLooks`), while its footprint follows the
 * wire's state as every thing's does, so collision stays the one the map server holds.
 */
export class WorldThings implements ThingSource {
  private views: ThingViews | null = null;
  private readonly playback: ThingPlayback;
  private readonly ids = new Map<number, string>();
  private readonly tops = new Map<number, { readonly x: number; readonly y: number }>();
  private readonly grounds = new Map<number, { readonly x: number; readonly y: number; readonly top: number }>();
  private signature = "";
  private lookVersion = 0;
  private playClock = 0;
  private endedSeen = 0;

  /**
   * The shrines' looks on this page: the set its character has discovered, and the pops playing.
   */
  public readonly shrines: ShrineLooks;

  /**
   * Shows one thing's current frame, as `play` iterates the things drawn, and reports it when it changed. The
   * frame ids are the playback's own, computed once per appearance, so a frame that changes nothing costs two
   * comparisons and allocates nothing; a changed frame writes one property of the report's record, which `sync`
   * replaces with an empty one whenever the things in view change, never per frame. A shrine's frame is its viewer's
   * look or its pop; every other thing's is the playback's.
   *
   * @param id The thing's prop id in the view.
   * @param handle Its handle.
   */
  private readonly showThing = (id: string, handle: number): void => {
    const frame = this.shrines.frame(handle, this.playClock, this.reduced()) ?? this.playback.frame(handle, this.playClock);
    if (frame !== null) {
      this.views?.showFrame(id, frame.id);
      if (gameReport.thingFrames[handle] !== frame.id) {
        gameReport.thingFrames[handle] = frame.id;
      }
    }
  };

  /**
   * Told of each change of state the playback sees, never of a first sight; the session sets it to sound the change
   * (ADR-085's amendment for P1.9-T79). Null tells no one.
   */
  public onTransition: ThingTransitionListener | null = null;

  /**
   * @param scene The scene the things draw in.
   * @param world The loaded world, for the appearances and frames.
   * @param map The map being played.
   * @param animator The tile animator the prop views follow.
   * @param props The prop views by key, which the things' views join.
   * @param band The index of the Players band's first layer.
   * @param baseCells The collision cells as compiled, which a restamp starts from.
   * @param liveCells The prediction grid's cells, which the things' footprints are stamped into.
   * @param lookup The target lookup E and the interaction prompt share, told when a thing changes.
   * @param reduced Whether the page's motion is reduced now, under which a shrine's pop strip is not drawn; never
   *   when left out.
   */
  public constructor(
    private readonly scene: Phaser.Scene,
    private readonly world: LoadedWorld,
    private readonly map: WorldManifest["maps"][number],
    private readonly animator: TileAnimator,
    private readonly props: Map<string, PropView>,
    private readonly band: number,
    private readonly baseCells: Uint8Array,
    private readonly liveCells: Uint8Array,
    private readonly lookup: TargetLookup,
    private readonly reduced: () => boolean = () => false,
  ) {
    const appearances = world.manifest.objectAppearances ?? [];
    this.shrines = new ShrineLooks(appearances);
    this.playback = new ThingPlayback(
      appearances,
      (appearance, anim) => console.warn(`thing appearance ${appearance} state ${anim} is not in world ${world.version}; drawing its initial state`),
      (handle, appearance, from, to) => this.onTransition?.(handle, appearances[appearance], from, to),
    );
    watchMoves(lookup, (handle) => this.playback.playing(handle, this.playClock));
  }

  /**
   * Forgets every transition the playback held, as a new session starts, so every thing's next record is first
   * sight (ADR-058 amendment 3), and with it the shrines' set and pops: every shrine is dormant until the session's
   * own `shrines` arrives (ADR-096 decision 18).
   */
  public resetPlayback(): void {
    this.playback.reset();
    this.shrines.reset();
    gameReport.shrines = [];
  }

  /**
   * Takes the session's `shrines` (ADR-096 decision 17): the set its character has discovered, which decides each
   * shrine's look from the next frame drawn, and the page's own discovery, which plays that shrine's pop once.
   *
   * @param handles The handles of the discovered shrines.
   * @param discovered Whether a discovery caused the message.
   * @param found The handle of the shrine just discovered, 65535 for none or for a shrine that is no thing.
   * @param now The game loop's time in ms.
   * @returns Whether the message tells a discovery the page did not already hold.
   */
  public discoveries(handles: readonly number[], discovered: boolean, found: number, now: number): boolean {
    const fresh = this.shrines.receive(handles, discovered, found, now);
    gameReport.shrines = this.shrines.handles();
    return fresh;
  }

  /**
   * Plays a shrine's pop, as an arrival's `thingEvent` asks (ADR-096 decisions 20, 24 and 25).
   *
   * @param handle The shrine's handle.
   * @param now The game loop's time in ms.
   * @returns False when the handle is no shrine in view or its pop is still playing, so the event is ignored.
   */
  public pulse(handle: number, now: number): boolean {
    return this.shrines.pulse(handle, now);
  }

  /**
   * Hands every thing an applied snapshot carries to the transition playback, which tells `onTransition` of each
   * change of state before this returns.
   *
   * @param record The applied snapshot.
   * @param entities The client's view.
   * @param now The game loop's time in ms.
   */
  public observe(record: SnapshotRecord, entities: ReadonlyMap<number, EntityState>, now: number): void {
    observeSnapshot(this.playback, record, entities, now);
  }

  /**
   * Draws the things in view: each thing's frame for the appearance its spawn record names and the state its
   * record carries in the animation bits, mirrored like the editor drew the prop it was lowered from and, as the
   * editor draws it, at its own layer's depth with its sort offset and at the author's pixel (`thingPlace`,
   * P1.1-T7), and re-stamps their footprints, on the feet point the map server holds, over the prediction grid so
   * prediction matches the map server. Both are rebuilt only when a thing appears, moves, changes state or leaves,
   * which only an applied snapshot does, so a snapshot runs this before the predictor replays its unacknowledged
   * inputs and the replay meets the footprints the snapshot's states name. The frame placed here and the footprint
   * stamped are the state's the byte names at once; a transition plays over the frame in `play` and never delays
   * the stamp (ADR-058 amendment 1). A change also tells the target lookup E and the interaction prompt share,
   * whose next resolve scans the view again, since a thing's state, place or order is what its answer rests on
   * besides the predicted cells. The report's frames by handle start again here, so a thing that left does not
   * stay listed; `play` refills them on the next render frame. A change of the page's discovered set rebuilds them
   * too, since it changes the frame a shrine is placed on. An NPC (ADR-136) is drawn as a character, not here, but its
   * place and look still count as a change, since the lookup offers Talk for it.
   *
   * @param entities The client's view.
   */
  public sync(entities: ReadonlyMap<number, EntityState>): void {
    const world = this.world;
    const appearances = world.manifest.objectAppearances;
    if (appearances === undefined) {
      return;
    }
    let signature = "";
    for (const state of entities.values()) {
      if (state.kind !== EntityKind.Player && state.kind !== EntityKind.GroundItem && !isCreatureKind(state.kind)) {
        signature += `${state.handle}:${state.kind}:${state.appearance}:${state.anim}:${state.x}:${state.y};`;
      }
    }
    if (signature === this.signature && this.lookVersion === this.shrines.version) {
      return;
    }
    this.signature = signature;
    this.lookVersion = this.shrines.version;
    this.shrines.beginView();
    invalidateTarget(this.lookup);
    const tileSize = world.manifest.tileSize;
    const instances: { instance: OrderedProp; layer: number }[] = [];
    const stamps: ThingStamp[] = [];
    const report: { handle: number; kind: number; state: number }[] = [];
    this.ids.clear();
    this.tops.clear();
    this.grounds.clear();
    for (const state of entities.values()) {
      if (isCreatureKind(state.kind) || isNpcKind(state.kind) || state.kind === EntityKind.GroundItem) {
        continue;
      }
      const thing = thingLook(appearances, state);
      if (state.kind !== EntityKind.Player) {
        report.push({ handle: state.handle, kind: state.kind, state: state.anim });
      }
      const rest = thing === null || state.kind !== EntityKind.Shrine ? -1 : this.shrines.hold(state.handle, state.appearance);
      const placed = thing === null ? null : (thing.look.states[rest] ?? thing.state).frame;
      const rect = placed === null ? undefined : world.assets.frames[frameId(placed.tileset, null, placed.tile)];
      const width = rect?.w;
      if (thing === null || placed === null || rect === undefined || width === undefined) {
        continue;
      }
      const id = `thing-${state.handle}`;
      this.ids.set(state.handle, id);
      const place = thingPlace(thing.look, width, state, tileSize, this.band);
      this.tops.set(state.handle, { x: place.x * tileSize + width / 2, y: (place.y + 1) * tileSize - rect.h + (rect.art?.y ?? 0) });
      instances.push({ instance: { id, frame: placed, x: place.x, y: place.y, flipX: thing.look.flipX, flipY: thing.look.flipY, sortPixels: place.sortPixels, order: place.order }, layer: place.layer });
      stamps.push({ x: state.x, y: state.y, footprint: thing.state.footprint, flipX: thing.look.flipX, flipY: thing.look.flipY, offset: thing.look.offset });
      if (state.kind === EntityKind.Dummy) {
        const top = ((place.y + 1) * tileSize - rect.h + (rect.art?.y ?? 0)) * (SIXTEENTHS / tileSize);
        const standing = thing.look.states[thing.look.initial]?.footprint ?? null;
        this.grounds.set(state.handle, { x: state.x, y: thingGroundY(state.y, standing, thing.look.flipY, tileSize, thing.look.offset?.y), top });
      }
    }
    this.shrines.endView();
    gameReport.things = report;
    gameReport.thingFrames = {};
    restampThings(this.baseCells, this.liveCells, this.map.width, this.map.height, stamps, tileSize, COMPILED_COLLISION_CELLS_PER_TILE, SOLID);
    this.views ??= new ThingViews((key) => {
      const view = new PropView(this.scene, world.assets, tileSize, this.map.height, this.animator, false);
      this.props.set(key, view);
      return view;
    });
    this.views.sync(instances, this.band);
  }

  /**
   * Draws each thing's current frame over its resting frame (ADR-058 decision 5): the transition frame while one
   * plays, the state's frame once it has, so a gate plays its opening once and then stands open. Runs every render
   * frame over the resting frames the last `sync` placed; a thing whose frame is unchanged costs one comparison and
   * nothing is allocated. When a strip has played to its end the target lookup is invalidated, so a thing that offered
   * no verb during its move (ADR-058's amendment of 2026-10-07) offers its next one from that frame.
   *
   * @param now The game loop's time in ms.
   */
  public play(now: number): void {
    if (this.views === null) {
      return;
    }
    this.playClock = now;
    this.ids.forEach(this.showThing);
    if (this.playback.ended !== this.endedSeen) {
      this.endedSeen = this.playback.ended;
      invalidateTarget(this.lookup);
    }
    gameReport.transitions = this.playback.started;
    gameReport.shrinePops = this.shrines.pops;
  }

  /**
   * Where the top of a thing's art is drawn, centred across its frame, as the last `sync` placed it.
   *
   * @param handle The thing's handle.
   * @param out Where to write it, in world pixels.
   * @returns False when no thing of that handle is drawn.
   */
  public top(handle: number, out: MutablePoint): boolean {
    const at = this.tops.get(handle);
    if (at === undefined) {
      return false;
    }
    out.x = at.x;
    out.y = at.y;
    return true;
  }

  /**
   * Where a training dummy's feet box stands, as the map server places it with the same `thingGroundY` (ADR-058's
   * amendment of 2026-10-03): on the lowest row its standing footprint blocks, its feet point when that blocks nothing
   * or it is mirrored upright, and where the top of its art is drawn, the body a click selects it on (P2.2-T3).
   *
   * @param handle The dummy's handle.
   * @param out Where to write the feet point and the top, in sixteenths.
   * @returns False when no dummy of that handle is drawn.
   */
  public ground(handle: number, out: MutablePoint & { top: number }): boolean {
    const at = this.grounds.get(handle);
    if (at === undefined) {
      return false;
    }
    out.x = at.x;
    out.y = at.y;
    out.top = at.top;
    return true;
  }

  /**
   * Removes every thing drawn and lifts their footprints from the grid, as a session ends; nothing happens when no
   * thing was ever in view since the last clear.
   */
  public clear(): void {
    if (this.signature === "") {
      return;
    }
    this.signature = "";
    this.ids.clear();
    this.tops.clear();
    this.grounds.clear();
    gameReport.thingFrames = {};
    this.views?.clear(this.band);
    this.liveCells.set(this.baseCells);
  }
}
